import { useQuery, useQueryClient } from '@tanstack/react-query'
import { DataTable, useDataTableContext } from 'components/DataTable'
import { useEffect, useMemo, useState } from 'react'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import {
  useElasticRelevancySort,
  useManageAssignmentActions,
  useSortParams,
  useUnifiedSummaryRefetch,
} from 'components/Tables/restaurantTableHooks'
import { Col, Row } from 'react-bootstrap'
import useApiService from 'services/api'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import * as S from '../CommonTable.styles'

import to from 'await-to-js'
import { AxiosError } from 'axios'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import TableButtonProduct from 'components/Buttons/TableButtons/TableButtonProduct'
import {
  filterStoreRepo,
  isShowHiddenActive,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import DistributorAssignModal from 'components/Modals/AssignmentModal/DistributorAssignModal'
import TagAssignModal from 'components/Modals/AssignmentModal/TagAssignModal'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import DoorExportModal from 'components/Modals/ExportModal/DoorExportModal'
import OpportunitiesTableFooterControls from 'components/Opportunities/OpportunitiesTableFooterControls'
import { DoorsFilterset } from 'components/Tables/DoorsTable/DoorsFilterset'
import { UNIFIED_DOORS_SUMMARY_KEY } from 'constants/tableQueryKeys'
import {
  PiCalendarStar,
  PiExport,
  PiEye,
  PiEyeSlash,
  PiForkKnife,
  PiHandshake,
  PiShippingContainer,
  PiStorefront,
  PiTag,
  PiTruck,
} from 'react-icons/pi'
import { useAuth } from 'context/authentication/useAuth'
import { notifyError } from 'services/toast'
import {
  activeProductIds,
  useSelectedProductIds,
} from 'stores/SelectedProductsStore/SelectedProductsStore'
import { useOpportunitiesContext } from '../../../pages/app/opportunities/opportunities-context'

import { DataTableContainer } from '../../DataTable/UI'

import { TableSearchWithElastic } from '../../Filters/TableSearchWithElastic'
import { DoorMenuModal } from '../../Modals/MenuModal/DoorMenuModal'
import { cn } from '../../UI/cn'
import { ChangeProductViewModal } from '../../change-product-view-modal'
import { SiSalesforce } from 'react-icons/si'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { toast } from 'react-toastify'
import { CreateDealModalForTable } from 'components/Deals/CreateDealModal/CreateDealModal'
import GPOAssignModal from 'components/Modals/AssignmentModal/GPOAssignModal'
import {
  sortableFieldOptions,
  sortableFields,
  useDoorsTableColumnsV2,
} from './DoorsTableColumnsV2'
import TableButtonSort from 'components/Buttons/TableButtons/TableButtonSort'
import { IDoorsTableProps } from './DoorsTable'
import ReminderModal from 'components/Reminders/ReminderModal'
import { useUnifiedDoorsReportsTable } from 'services/opportunities/useUnifiedDoorsReportsTable'

const PAGE_SIZE = 100

export function DoorsTableComponentV2({
  hideTitle = true,
  ...props
}: IDoorsTableProps) {
  const featureFlag = useFeatureFlagService()
  const tableContext = useDataTableContext()
  const queryClient = useQueryClient()
  const api = useApiService()
  const isChainTable = props.chainFilter != null
  // FIR-7845: hide/unhide writes are admin-only (server: IsCompanyAdmin).
  // When the user-types flag is off, useAuth reports everyone as Admin,
  // matching the server permission's flag-off passthrough.
  const { isAdmin } = useAuth()

  const { selectedTag } = useOpportunitiesContext()
  const [menuModal, setMenuModal] = useState<
    | {
        doorId: number
        doorName?: string
        itemsIds?: number[]
        menuLength?: number
      }
    | undefined
  >(undefined)

  // TABLE CONTEXT (SORTING, ETC)
  const {
    state: { rowSelection, totalSelectedRows, isAllRowsSelected },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = tableContext

  const selectedProductsIds = useSelectedProductIds()

  const [showProductSelection, setShowProductSelection] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showAssignTags, setShowAssignTags] = useState(false)
  const [showCreateDealModal, setShowCreateDealModal] = useState(false)
  const [showAssignDistributors, setShowAssignDistributors] = useState(false)
  const [showAssignGPOs, setShowAssignGPOs] = useState(false)
  const [showExportAllModal, setShowExportAllModal] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)

  const restaurantsFilterStore = filterStoreRepo.getStore('DOORS_TABLE')
  const { page, setPage, search, valueFilters } = restaurantsFilterStore()
  const filtersAsParams = useFilterParams(restaurantsFilterStore)

  // FIR-7845: hidden-only mode is driven by the "Show Hidden" quick filter
  // in the filterset modal — a regular `show_hidden` value filter in the
  // store, so it flows into request params via useFilterParams (rows, count,
  // summary/stat cards) and participates in chips/clear-all/saved views.
  // Here it only flips the kebab action between Hide and Unhide.
  const showHidden = isShowHiddenActive(valueFilters)

  const { sorting, setSorting, filteredSortOptions } = useElasticRelevancySort(
    props.tableKey,
    search,
    'chain_count',
    sortableFieldOptions
  )

  // FILTER PARAMS (FOR API CALL)
  const filterParams = useMemo(() => {
    const params: Record<string, unknown> = { ...filtersAsParams }

    if (props.chainFilter) {
      params.chain = props.chainFilter
    }

    return params
  }, [filtersAsParams, props.chainFilter])

  const sortParams = useSortParams(sorting)

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      ...sortParams,
      // Carry the product selection so the reports doors summary's total_ltv is
      // product-aware (drops the -1 "all products" sentinel). The list ignores
      // it — product only scopes LTV, never rows — but the summary reads it.
      // Without this the LTV card would always show the all-products total.
      product_id: activeProductIds(selectedProductsIds),
      // FIR-7845: `show_hidden` (hidden-only mode) arrives inside
      // `filterParams` like any other value filter and flows straight through
      // to `reports/doors/` + `reports/doors/summary/`; the summary hook below
      // is called with this same object, so its query picks up show_hidden too.
    }),
    [filterParams, sortParams, selectedProductsIds]
  )

  // Rows + count come from the reports read-model (`reports/doors/` +
  // `reports/doors/summary/`); rows already carry the nested `restaurant`,
  // `restaurant.firstbite_id`, `door_proxy_id`, and `chain_proxy.id` the
  // existing columns/selection/actions resolve against.
  const unifiedDoors = useUnifiedDoorsReportsTable(
    filterAndSortParams,
    selectedTag === 'fb-locations' || !!props.chainFilter
  )

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterAndSortParams,
    page: page,
    products: selectedProductsIds,
  })

  // API CALL
  const {
    data: places,
    isFetching,
    refetch,
  } = useQuery({
    staleTime: Infinity,
    queryKey: TABLE_QUERY_KEY,
    enabled: selectedTag === 'fb-locations' || !!props.chainFilter, // Enable only when tab is selected or chain filter is present (chain page)
    queryFn: async ({ signal }) => {
      clearSelectedRows()
      const [err, res] = await to(
        unifiedDoors.listAction(
          {
            ...filterAndSortParams,
            page: page,
            limit: PAGE_SIZE,
            product_id: selectedProductsIds,
          },
          signal
        )
      )

      if ((err as AxiosError)?.code === 'ERR_NETWORK') {
        notifyError('Sorry! Unable to complete this search, please try again.')
      }
      if (err) throw err

      return res.results
    },
  })

  useEffect(() => {
    clearSelectedRows()
  }, [places])

  // ALL stat-card values (door count, distinct-chain count, LTV) come from the
  // unified doors summary — filter-scoped and product-aware. FIR-7845: the
  // summary reads `filterAndSortParams` (via `useUnifiedDoorsReportsTable`
  // above), which carries `show_hidden`, so the stat cards follow the Show
  // Hidden quick filter like the rows.
  const effectiveDoorCount = unifiedDoors.unifiedCount ?? 0
  const effectiveChainsCount = unifiedDoors.unifiedChainsCount ?? 0
  const effectiveLtv = unifiedDoors.unifiedLtv ?? 0
  const summaryLoading = unifiedDoors.unifiedSummaryFetching

  // ˆ PASS LTV RESULTS TO PARENT COMPONENT ˆ
  useEffect(() => {
    props.setDoorsSummary?.({
      total_ltv: effectiveLtv,
      places_count: effectiveDoorCount,
      chains_count: effectiveChainsCount,
      isLoading: summaryLoading,
    })
    setTotalRowsInBackend(effectiveDoorCount)
  }, [summaryLoading, effectiveLtv, effectiveDoorCount, effectiveChainsCount])

  const selectedDoorIds = useMemo(() => {
    const accessor = 'restaurant.firstbite_id'
    return (
      places
        ?.filter((_, i: number) => rowSelection[i])

        ?.map((r: any) => {
          return accessor.split('.').reduce((acc, key) => acc[key], r)
        }) ?? []
    )
  }, [places, rowSelection])

  const unselectedDoorIds = useMemo(() => {
    if (!isAllRowsSelected) return []
    return (
      places
        ?.filter((_, i: number) => !rowSelection[i])
        ?.map((r: any) => r.restaurant.firstbite_id) ?? []
    )
  }, [places, rowSelection, isAllRowsSelected])

  // FIR-7845: door-scoped actions (Manage Tags, Distributors, GPOs,
  // Reminders below) key off a top-level `door_proxy_id` — present on both
  // legacy rows and unified rows (`useUnifiedDoorsReportsTable` sets
  // `door_proxy_id: row['id']`) — NOT `restaurant.firstbite_id`
  // (`selectedDoorIds` above), which is the restaurant's own id used for
  // exports/contact-requests. Hide/Unhide must use the same door-proxy id.
  const selectedDoorProxyIds = useMemo(() => {
    return (
      places
        ?.filter((_, i: number) => rowSelection[i])
        ?.map((r: any) => r.door_proxy_id) ?? []
    )
  }, [places, rowSelection])

  const columns = useDoorsTableColumnsV2(
    setMenuModal,
    !!isChainTable,
    props.chainFilter,
    selectedProductsIds,
    sorting,
    false
  )

  const selectedDoorProxy = useMemo(() => {
    if (selectedDoorIds.length !== 1 || !places) return undefined
    const doorProxy = places.find(
      (item) => item.restaurant.firstbite_id === selectedDoorIds[0]
    )
    return doorProxy
  }, [selectedDoorIds, places])

  const refetchDoorsSummary = useUnifiedSummaryRefetch(
    queryClient,
    UNIFIED_DOORS_SUMMARY_KEY,
    filterAndSortParams
  )

  const { apiTagsAction, apiDistributorsAction, apiGPOsAction } =
    useManageAssignmentActions({
      api,
      tableType: 'doors-reports-table',
      refetchSummary: refetchDoorsSummary,
    })

  const linkVariant = useTableLinkVariant()
  const bulkActions = useMemo(
    () => [
      {
        label: 'Create Deals',
        icon: <PiHandshake size={20} />,
        callback: () => setShowCreateDealModal(true),
      },
      {
        label: 'Manage Tags',
        icon: <PiTag size={20} />,
        callback: () => setShowAssignTags(true),
      },
      {
        label: 'Add Known Distributors',
        icon: <PiTruck size={20} />,
        callback: () => setShowAssignDistributors(true),
      },
      {
        label: 'Add GPO',
        icon: <PiShippingContainer size={20} />,
        callback: () => setShowAssignGPOs(true),
      },
      {
        label: 'Schedule Reminder',
        icon: <PiCalendarStar size={20} />,
        callback: () => setShowReminderModal(true),
        disabled: selectedDoorIds.length !== 1,
        disabledTooltipText:
          'Please select exactly one door to schedule a reminder',
      },
      {
        label: 'Export Selected',
        icon: <PiExport size={20} />,
        callback: () => setShowExportModal(true),
      },
      {
        label: 'Push to Salesforce',
        icon: <SiSalesforce size={20} />,
        hidden: !featureFlag.enablePushToSalesforce,
        callback: () => {
          setTimeout(() => {
            toast.success('Successfully pushed to Salesforce')
          }, 1000)
        },
      },
      {
        label: showHidden ? 'Unhide' : 'Hide',
        icon: showHidden ? <PiEye size={20} /> : <PiEyeSlash size={20} />,
        // Admin-only write; viewing hidden rows stays open to everyone.
        hidden: !isAdmin,
        disabled: isAllRowsSelected || selectedDoorProxyIds.length === 0,
        disabledTooltipText:
          'Select specific rows to hide (select-all is not supported)',
        callback: async () => {
          await api.commonSetHidden({
            doorProxyIds: selectedDoorProxyIds,
            hidden: !showHidden,
            tableType: 'doors-reports-table',
          })
          toast.success(
            showHidden
              ? `Unhid ${selectedDoorProxyIds.length} record(s)`
              : `Hid ${selectedDoorProxyIds.length} record(s)`
          )
        },
      },
    ],
    [
      api,
      isAdmin,
      isAllRowsSelected,
      selectedDoorIds.length,
      selectedDoorProxyIds,
      showHidden,
      featureFlag.enablePushToSalesforce,
    ]
  )

  return (
    <DataTableContainer>
      <DataTable
        tableHeader={
          <>
            {!hideTitle && (
              <Row className="mb-4">
                <Col>
                  <S.CardTitle>
                    Doors{' '}
                    <span style={{ fontWeight: 100 }}>
                      ({effectiveDoorCount})
                    </span>
                  </S.CardTitle>
                </Col>
              </Row>
            )}
            <S.SearchContainer>
              <div className="flex w-full gap-2 items-center">
                <TableSearchWithElastic
                  filterStore={restaurantsFilterStore}
                  historyKey="doors"
                  options={[
                    {
                      label: 'Menu',
                      searchKey: 'menu',
                      searchPlaceholder: 'Search menus… e.g. chicken',
                      elastic: true,
                      autoSetRelevancySort: false,
                      icon: PiForkKnife,
                    },
                    {
                      label: 'Name',
                      searchKey: 'restaurant_name',
                      searchPlaceholder:
                        'Search restaurant names… e.g. Bella’s Bistro',
                      elastic: true,
                      autoSetRelevancySort: true,
                      icon: PiStorefront,
                    },
                  ]}
                  onSearchApplied={(option) => {
                    if (option.autoSetRelevancySort) {
                      // Name searches: set to relevancy sort
                      setSorting([{ id: 'relevancy', desc: true }])
                    } else {
                      // All other searches (menu, domain): reset to chain count
                      setSorting([{ id: 'chain_count', desc: true }])
                    }
                  }}
                />

                <TableButtonProduct
                  selectedProducts={selectedProductsIds}
                  onClick={() => setShowProductSelection(true)}
                />

                <DoorsFilterset filterIdentifier="DOORS_TABLE" />

                <ColumnSelectorRecipient tableKey={props.tableKey} />
                <TableButtonSort
                  options={filteredSortOptions}
                  onSelect={(value, direction) => {
                    return setSorting([
                      { id: value, desc: direction === 'desc' },
                    ])
                  }}
                  selectedValue={
                    sorting.length
                      ? {
                          value: sorting[0].id,
                          direction: sorting[0].desc ? 'desc' : 'asc',
                        }
                      : undefined
                  }
                />
              </div>

              <div className={cn('flex flex-row justify-between gap-4')}>
                {linkVariant !== 'plain' && (
                  <TableButtonAction
                    items={bulkActions}
                    disabled={!totalSelectedRows}
                    selectedRowsCount={totalSelectedRows ?? 0}
                  />
                )}
                <FilterChips
                  identifier={'DOORS_TABLE'}
                  clearAllButton
                  showActive
                  store={restaurantsFilterStore}
                />
              </div>
            </S.SearchContainer>

            <TagAssignModal
              show={showAssignTags}
              handleClose={() => setShowAssignTags(false)}
              tableQueryKey={TABLE_QUERY_KEY}
              data={places ?? []}
              filterAndSortParams={filterAndSortParams}
              apiTagsAction={apiTagsAction}
              idAccessor="door_proxy_id"
              tableObjAccessor="door_taglist"
            />

            <DistributorAssignModal
              show={showAssignDistributors}
              handleClose={() => setShowAssignDistributors(false)}
              tableQueryKey={TABLE_QUERY_KEY}
              data={places ?? []}
              idAccessor="door_proxy_id"
              filterAndSortParams={filterAndSortParams}
              apiDistributorsAction={apiDistributorsAction}
              tableObjAccessor="door_distributors"
            />

            <GPOAssignModal
              show={showAssignGPOs}
              handleClose={() => setShowAssignGPOs(false)}
              tableQueryKey={TABLE_QUERY_KEY}
              data={places ?? []}
              idAccessor="door_proxy_id"
              filterAndSortParams={filterAndSortParams}
              apiGPOsAction={apiGPOsAction}
              tableObjAccessor="door_gpos"
            />

            <CreateDealModalForTable
              optimisticUiTarget="none"
              show={showCreateDealModal}
              handleClose={() => setShowCreateDealModal(false)}
              onDealCreated={refetch}
              queryKey={TABLE_QUERY_KEY}
              filterAndSortParams={filterAndSortParams}
              data={places ?? []}
              idAccessor="chain_proxy.id"
              doorIdAccessor="door_proxy_id"
              companyTypeSlug={'restaurants-bars'}
              tableTypeSlug={'doors-reports-table'}
              forDoorSelection={true}
            />
          </>
        }
        tableKey={props.tableKey}
        selectHeaderActions={
          linkVariant === 'plain' ? (
            <TableButtonAction
              items={bulkActions}
              disabled={!totalSelectedRows}
              selectedRowsCount={totalSelectedRows ?? 0}
            />
          ) : undefined
        }
        data={places ?? []}
        loading={isFetching}
        columns={columns}
        sortableFields={[...sortableFields]}
        virtualizeRows={true}
        enableRowSelection={!props.chainFilter}
        enableColumnResizing
        rowHeight={44}
        headerHeight={40}
        stickyFirstColumn={linkVariant === 'plain'}
        isPaginationEnabled={true}
        defaultSort={[{ id: 'chain_count', desc: true }]}
        paginationOptions={{
          totalRows: effectiveDoorCount,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          // Parity with the pre-cutover behavior: the pagination widget never
          // showed a loading state on this table (the summary loading state is
          // surfaced via the stat cards instead).
          isPaginationLoading: false,
        }}
        selectAllText="Select Targets"
        footerControls={
          <OpportunitiesTableFooterControls
            companyTypeSlug={'restaurants-bars'}
            chainProxy={props.chainProxy}
            unmatchedTableKey={props.unmatchedTableKey}
            onDownloadButtonClick={() =>
              selectedTag === 'fb-companies'
                ? setShowExportAllModal(true)
                : setShowExportModal(true)
            }
          />
        }
      />

      <ChangeProductViewModal
        show={showProductSelection}
        handleClose={() => setShowProductSelection(false)}
      />

      {menuModal?.doorId && (
        <DoorMenuModal
          filterStore={restaurantsFilterStore}
          show={!!menuModal}
          handleClose={() => setMenuModal(undefined)}
          doorId={menuModal.doorId!}
          doorName={menuModal.doorName!}
          menuLength={menuModal?.menuLength}
        />
      )}

      {featureFlag.enableReminders && (
        <ReminderModal
          open={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          defaultDoorProxyId={selectedDoorProxy?.door_proxy_id}
          defaultCompanyId={selectedDoorProxy?.chain_proxy?.id}
          onCreated={() => {
            setShowReminderModal(false)
          }}
        />
      )}

      <DoorExportModal
        tableTypeSlug="doors-reports-table"
        filters={filterAndSortParams}
        productSelected={selectedProductsIds[0]}
        show={showExportModal || !!showExportAllModal}
        _handleClose={() => {
          setShowExportModal(false)
          setShowExportAllModal?.(false)
        }}
        selectedIds={
          showExportModal && !isAllRowsSelected ? selectedDoorIds : undefined
        }
        excludeIds={isAllRowsSelected ? unselectedDoorIds : undefined}
        count={showExportModal ? selectedDoorIds?.length : effectiveDoorCount}
      />
    </DataTableContainer>
  )
}
